import { useEffect, useState } from 'react';

import { LoaderFunctionArgs, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Button, Center, Flex, HStack, Spinner, Stack, Text } from '@chakra-ui/react';

import { QueryClient } from '@tanstack/react-query';

import { PaginationState, SortingState } from '@tanstack/react-table';

import {
  DropdownOption,
  FilterData,
  Filters,
  MultiSelectionData,
  TransactionCategory,
  TransactionDirection,
  ArrowLeftOutline,
} from '@aslanpay/portal-common-lib';

import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import ProfileField from 'app/components/UI/ProfileField';
import DeductionAdjustmentsList from 'app/components/DeductionAdjustments/DeductionAdjustmentsList';
import ProfileSection from 'app/components/UI/ProfileSection';
import { Table } from 'app/components/UI/Table/Table';
import { TransactionActions } from 'app/components/UI/Table/Table.types';

import { defaultPageSize } from 'app/constants/table';
import { TransactionCategoryLabels, TransactionDirectionLabels } from 'app/constants/transactions';

import useFetchIndividualDeductionsReport, {
  fetchIndividualDeductionsReportQuery,
} from 'app/hooks/useFetchIndividualDeductionsReport';
import useFetchTransactionsPage from 'app/hooks/useFetchTransactionsPage';

import { formatCurrency } from 'app/utils/strings';
import getTransactionColumns from 'app/utils/table/transactions';

import type { Filter } from '@aslanpay/portal-common-lib';

const transactionDirectionOptions: DropdownOption[] = Object.entries(
  TransactionDirectionLabels
).map(([value, text]) => ({
  value,
  text,
}));

const transactionCategoryOptions: DropdownOption[] = Object.entries(TransactionCategoryLabels).map(
  ([value, text]) => ({
    value,
    text,
  })
);

export const individualDeductionsReportProfileLoader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const query = fetchIndividualDeductionsReportQuery(params.individualDeductionsReportId);
    return queryClient.getQueryData(query.queryKey) ?? (await queryClient.fetchQuery(query));
  };

function IndividualDeductionsReportProfile() {
  const navigate = useNavigate();

  const { individualDeductionsReportId } = useParams();

  const { data: report, isLoading: isLoadingReport } = useFetchIndividualDeductionsReport(
    individualDeductionsReportId
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = searchParams.get('page') ? Number(searchParams.get('page')) - 1 : 0;

  const [directionFilter, setDirectionFilter] = useState<TransactionDirection[]>(
    searchParams.get('directions')
      ? (searchParams.get('directions')?.split(',') as TransactionDirection[])
      : []
  );
  const [categoriesFilter, setCategoriesFilter] = useState<TransactionCategory[]>(
    searchParams.get('categories')
      ? (searchParams.get('categories')?.split(',') as TransactionCategory[])
      : []
  );
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: currentPage,
    pageSize: defaultPageSize,
  });
  const [sorting, setSorting] = useState<SortingState>(() => [
    ...(searchParams.get('sortBy')
      ? [
          {
            id: searchParams.get('sortBy') as string,
            desc: searchParams.get('sort') === 'DESC',
          },
        ]
      : []),
  ]);
  const [activeFilter, setActiveFilter] = useState<string | undefined>();

  const resetPagination = () => {
    setPagination({ pageIndex: 0, pageSize: defaultPageSize });
  };

  const { data: transactions, isInitialLoading: isLoadingTransactions } = useFetchTransactionsPage(
    {
      ids: report?.transactionIds,
      directions: directionFilter,
      categories: categoriesFilter,
      page: pageIndex,
      size: pageSize,
    },
    !!report?.transactionIds?.length
  );

  const filters: Filter[] = [
    {
      name: 'Transaction direction',
      type: 'category',
      data: transactionDirectionOptions,
      selectedValues: directionFilter,
    },
    {
      name: 'Transaction category',
      type: 'category',
      data: transactionCategoryOptions,
      selectedValues: categoriesFilter,
    },
  ];

  const actions: TransactionActions = {
    select: () => {},
    selectedTransaction: undefined,
  };

  const handleFilterClick = (filterName: string) => {
    setActiveFilter(filterName);
  };

  const handleDirectionChange = (values: (string | number)[]) => {
    setDirectionFilter(values as TransactionDirection[]);
    resetPagination();
  };

  const handleCategoryChange = (values: (string | number)[]) => {
    setCategoriesFilter(values as TransactionCategory[]);
    resetPagination();
  };

  const handleFilterChange = (filterName: string, newValues: string[]) => {
    switch (filterName) {
      case 'Transaction direction':
        handleDirectionChange(newValues);
        break;
      case 'Transaction category':
        handleCategoryChange(newValues);
        break;
      default:
        break;
    }
  };

  const totalDebit = report?.totalDebit || 0;
  const refundDebits = report?.refundDebits || 0;
  const chargebackDebits = report?.chargebackDebits || 0;
  const cashbackDebits = report?.cashbackDebits || 0;
  const salaryDebits = report?.salaryDebits || 0;
  const reserveDebits = report?.reserveDebits || 0;
  const savingsDebits = report?.savingsDebits || 0;

  const totalCredit = report?.totalCredit || 0;
  const refundCredits = report?.refundCredits || 0;
  const chargebackCredits = report?.chargebackCredits || 0;
  const cashbackCredits = report?.cashbackCredits || 0;
  const salaryCredits = report?.salaryCredits || 0;
  const reserveCredits = report?.reserveCredits || 0;
  const savingsCredits = report?.savingsCredits || 0;

  const totalDifference = report?.totalDifference || 0;
  const refundDifference = report?.refundDifference || 0;
  const chargebackDifference = report?.chargebackDifference || 0;
  const cashbackDifference = report?.cashbackDifference || 0;
  const salaryDifference = report?.salaryDifference || 0;
  const reserveDifference = report?.reserveDifference || 0;
  const savingsDifference = report?.savingsDifference || 0;

  const totalRolloverBalance = report?.rolloverBalance || 0;
  const refundRolloverBalance = report?.refundRolloverBalance || 0;
  const chargebackRolloverBalance = report?.chargebackRolloverBalance || 0;
  const cashbackRolloverBalance = report?.cashbackRolloverBalance || 0;
  const reserveRolloverBalance = report?.closingReserveBalance || 0;
  const savingsRolloverBalance = report?.closingSavingsBalance || 0;

  const transactionsAmountSum =
    transactions?.list
      .map((transaction) => transaction.amount)
      .reduce((sum, value) => sum + value, 0) || 0;

  const transactionsFxFeeSum =
    transactions?.list
      .map((transaction) => transaction.fxFee)
      .reduce((sum, value) => sum + value, 0) || 0;

  const transactionsTotalAmountSum =
    transactions?.list
      .map((transaction) => transaction.totalAmount)
      .reduce((sum, value) => sum + value, 0) || 0;

  const formatFilterValue = (
    filterName: string,
    filterValue: string | number,
    filterData: FilterData
  ) => {
    return (
      (filterData as MultiSelectionData).find((option) => option.value === filterValue)?.text ||
      String(filterValue)
    );
  };

  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    params.set('page', (pageIndex + 1).toString());

    if (directionFilter.length > 0) {
      params.set('directions', directionFilter.join(','));
    } else {
      params.delete('directions');
    }

    if (categoriesFilter.length > 0) {
      params.set('categories', categoriesFilter.join(','));
    } else {
      params.delete('categories');
    }

    if (sorting[0]) {
      params.set('sortBy', sorting[0].id);
      params.set('sort', sorting[0].desc ? 'DESC' : 'ASC');
    } else {
      params.delete('sortBy');
      params.delete('sort');
    }

    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [pageIndex, directionFilter, categoriesFilter, sorting, setSearchParams, searchParams]);

  return (
    <AuthenticatedPageWrapper title="Individual deduction report">
      {isLoadingReport ? (
        <Center width="full" height="full" flexGrow={1}>
          <Spinner color="brand.purple" size="xl" mb="50px" />
        </Center>
      ) : (
        <>
          <Button
            my="10px"
            alignSelf="flex-start"
            size="sm"
            variant="link"
            leftIcon={<ArrowLeftOutline boxSize="20px" />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <Flex gap="20px" align="flex-start" justify="space-between">
            <Text as="h2" textStyle="h2" color="brand.darkPurple">
              {`${report?.employeeName} - ${report?.payCycleName}`}
            </Text>
          </Flex>

          <Flex mt="20px" gap="20px" alignItems="flex-start" flexWrap="wrap">
            <ProfileSection title="Debits">
              <HStack spacing="20px">
                <ProfileField label="Total" value={formatCurrency(totalDebit, report?.currency)} />
                <ProfileField
                  label="Refunds"
                  value={formatCurrency(refundDebits, report?.currency)}
                />
                <ProfileField
                  label="Chargebacks"
                  value={formatCurrency(chargebackDebits, report?.currency)}
                />
                <ProfileField
                  label="Cashback"
                  value={formatCurrency(cashbackDebits, report?.currency)}
                />
                <ProfileField
                  label="Set & Spend"
                  value={formatCurrency(reserveDebits, report?.currency)}
                />
                <ProfileField
                  label="Savings"
                  value={formatCurrency(savingsDebits, report?.currency)}
                />
                <ProfileField
                  label="Salary"
                  value={formatCurrency(salaryDebits, report?.currency)}
                />
              </HStack>
            </ProfileSection>
            <ProfileSection title="Credits">
              <HStack spacing="20px">
                <ProfileField label="Total" value={formatCurrency(totalCredit, report?.currency)} />
                <ProfileField
                  label="Refunds"
                  value={formatCurrency(refundCredits, report?.currency)}
                />
                <ProfileField
                  label="Chargebacks"
                  value={formatCurrency(chargebackCredits, report?.currency)}
                />
                <ProfileField
                  label="Cashback"
                  value={formatCurrency(cashbackCredits, report?.currency)}
                />
                <ProfileField
                  label="Set & Spend"
                  value={formatCurrency(reserveCredits, report?.currency)}
                />
                <ProfileField
                  label="Savings"
                  value={formatCurrency(savingsCredits, report?.currency)}
                />
                <ProfileField
                  label="Salary"
                  value={formatCurrency(salaryCredits, report?.currency)}
                />
              </HStack>
            </ProfileSection>
            <ProfileSection title="Differences">
              <HStack spacing="20px">
                <ProfileField
                  label="Total"
                  value={formatCurrency(totalDifference, report?.currency)}
                />
                <ProfileField
                  label="Refunds"
                  value={formatCurrency(refundDifference, report?.currency)}
                />
                <ProfileField
                  label="Chargebacks"
                  value={formatCurrency(chargebackDifference, report?.currency)}
                />
                <ProfileField
                  label="Cashback"
                  value={formatCurrency(cashbackDifference, report?.currency)}
                />
                <ProfileField
                  label="Set & Spend"
                  value={formatCurrency(reserveDifference, report?.currency)}
                />
                <ProfileField
                  label="Savings"
                  value={formatCurrency(savingsDifference, report?.currency)}
                />
                <ProfileField
                  label="Salary"
                  value={formatCurrency(salaryDifference, report?.currency)}
                />
              </HStack>
            </ProfileSection>
            <ProfileSection title="Rollover balances">
              <HStack spacing="20px">
                <ProfileField
                  label="Total"
                  value={formatCurrency(totalRolloverBalance, report?.currency)}
                />
                <ProfileField
                  label="Refunds"
                  value={formatCurrency(refundRolloverBalance, report?.currency)}
                />
                <ProfileField
                  label="Chargebacks"
                  value={formatCurrency(chargebackRolloverBalance, report?.currency)}
                />
                <ProfileField
                  label="Cashback"
                  value={formatCurrency(cashbackRolloverBalance, report?.currency)}
                />
                <ProfileField
                  label="Set & Spend"
                  value={formatCurrency(reserveRolloverBalance, report?.currency)}
                />
                <ProfileField
                  label="Savings"
                  value={formatCurrency(savingsRolloverBalance, report?.currency)}
                />
              </HStack>
            </ProfileSection>
          </Flex>
          <Flex
            px="20px"
            py="10px"
            gap="60px"
            border="1px solid"
            borderRadius="10px"
            borderColor="brand.lightGray1"
          >
            <Stack spacing="2px">
              <Text textStyle="smallMedium" color="brand.darkGray">
                Cutoff amount
              </Text>
              <Text as="h3" textStyle="h3Medium" color="brand.black">
                {formatCurrency(report?.cutoffAmount || 0, report?.currency)}
              </Text>
            </Stack>
            <Stack spacing="2px">
              <Text textStyle="smallMedium" color="brand.darkGray">
                Starting balance
              </Text>
              <Text as="h3" textStyle="h3Medium" color="brand.black">
                {formatCurrency(report?.previousRolloverBalance || 0, report?.currency)}
              </Text>
            </Stack>
            <Stack spacing="2px">
              <Text textStyle="smallMedium" color="brand.darkGray">
                Points earned
              </Text>
              <Text as="h3" textStyle="h3Medium" color="brand.black">
                {/* TODO: The "|| 0 should be removed when the backend puts the field */}
                {report?.pointsEarned || 0}
              </Text>
            </Stack>
            <Stack spacing="2px">
              <Text textStyle="smallMedium" color="brand.darkGray">
                Total deduction value
              </Text>
              <Text as="h3" textStyle="h3Medium" color="brand.black">
                {formatCurrency(report?.salaryDeductions || 0, report?.currency)}
              </Text>
            </Stack>
          </Flex>
          <Flex align="center" gap="10px">
            <Text as="h5" textStyle="h5SemiBold" color="brand.darkGray">
              Transactions list
            </Text>
            {!isLoadingTransactions && (
              <Text
                p="8px"
                borderRadius="10px"
                alignSelf="flex-start"
                bgColor="alert.error"
                color="system.error"
                textStyle="extraSmallBold"
              >
                These totals only apply to the current page.
              </Text>
            )}
          </Flex>
          <Flex flexDir="row" gap="10px" align="center">
            {isLoadingTransactions ? (
              <Center width="full" height="full" flexGrow={1}>
                <Spinner color="brand.purple" size="xl" mb="50px" />
              </Center>
            ) : (
              <Flex
                px="20px"
                py="10px"
                gap="60px"
                border="1px solid"
                borderRadius="10px"
                borderColor="brand.lightGray1"
                marginRight="auto"
              >
                <Stack spacing="2px">
                  <Text textStyle="smallSemiBold" color="brand.darkGray">
                    Total transactions
                  </Text>
                  <Text as="h3" textStyle="smallMedium" color="brand.black">
                    {transactions?.total || 0}
                  </Text>
                </Stack>
                <Stack spacing="2px">
                  <Text textStyle="smallSemiBold" color="brand.darkGray">
                    Sum amount
                  </Text>
                  <Text as="h3" textStyle="smallMedium" color="brand.black">
                    {formatCurrency(transactionsAmountSum || 0, report?.currency)}
                  </Text>
                </Stack>
                <Stack spacing="2px">
                  <Text textStyle="smallSemiBold" color="brand.darkGray">
                    Sum FX fees
                  </Text>
                  <Text as="h3" textStyle="smallMedium" color="brand.black">
                    {formatCurrency(transactionsFxFeeSum || 0, report?.currency)}
                  </Text>
                </Stack>
                <Stack spacing="2px">
                  <Text textStyle="smallSemiBold" color="brand.darkGray">
                    Sum total amount
                  </Text>
                  <Text as="h3" textStyle="smallMedium" color="brand.black">
                    {formatCurrency(transactionsTotalAmountSum || 0, report?.currency)}
                  </Text>
                </Stack>
              </Flex>
            )}
          </Flex>
          <Filters
            activeFilter={activeFilter}
            displayFormatter={formatFilterValue}
            filters={filters}
            onFilterClose={() => setActiveFilter(undefined)}
            onFilterChange={handleFilterChange}
            onFilterClick={handleFilterClick}
          />
          {isLoadingTransactions ? (
            <Center width="full" height="full" flexGrow={1}>
              <Spinner color="brand.purple" size="xl" mb="50px" />
            </Center>
          ) : (
            <Table
              columns={getTransactionColumns(actions)}
              data={transactions?.list || []}
              total={transactions?.total || 0}
              pageNumber={transactions?.pageNumber || pageIndex}
              pageSize={transactions?.pageSize || pageSize}
              onPaginationChange={setPagination}
              sorting={sorting}
              onSortingChange={setSorting}
            />
          )}
          {report?.id && <DeductionAdjustmentsList individualReportIds={[report.id]} />}
        </>
      )}
    </AuthenticatedPageWrapper>
  );
}

export default IndividualDeductionsReportProfile;
